import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { isHoodieApiConfigured } from "../../../../api/hoodieClient";
import { useClassicSiteAuth } from "../../auth";
import {
  HOODIE_API_BASE_URL,
  stagingProbeDelete,
  stagingProbeEventNoOpPatch,
  stagingProbeGet,
  stagingProbeListingNoOpPatch,
  stagingProbePatchJson,
  stagingProbePatchMultipart,
  stagingProbePostJson,
  stagingProbePromoNoOpPatch,
} from "../../api/classicSiteStagingDiagnostics";
import StagingTestControl, {
  StagingFileInput,
  StagingInput,
} from "../../components/diagnostics/StagingTestControl";
import StagingTestResult from "../../components/diagnostics/StagingTestResult";
import CsrfCookieDiagnosticDisplay from "../../components/CsrfCookieDiagnosticDisplay";
import SessionEnvironmentDiagnosticPanel from "../../components/diagnostics/SessionEnvironmentDiagnosticPanel";
import { CLASSIC_SITE_BASE } from "../../classicSite.constants";

const BILLING_LEGACY_LINKS = [
  { label: "Account profile", path: "/member/accounts/profile/" },
  { label: "Subscriptions", path: "/member/accounts/subscriptions/" },
  { label: "Choose subscription", path: "/member/accounts/subscriptions/choose/" },
  { label: "Choose plan", path: "/plans/choose/" },
];

function SessionCsrfSection() {
  const { status, identity, csrfDiagnostic, httpStatus, refresh, lastCheckedAt, isAuthenticated } =
    useClassicSiteAuth();
  const [refreshResult, setRefreshResult] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const runSessionProbe = useCallback(async () => {
    setRefreshLoading(true);
    setRefreshResult(null);
    try {
      const result = await stagingProbeGet("hoodies/me/");
      setRefreshResult(result);
    } finally {
      setRefreshLoading(false);
    }
  }, []);

  const runRefresh = useCallback(async () => {
    setRefreshLoading(true);
    setRefreshResult(null);
    try {
      await refresh();
      const result = await stagingProbeGet("hoodies/me/");
      setRefreshResult(result);
    } finally {
      setRefreshLoading(false);
    }
  }, [refresh]);

  return (
    <section className="cs-diag-section">
      <h2>1. Session + CSRF verification</h2>
      <p className="cs-diag-note">
        Session cookie is HttpOnly — presence is inferred from{" "}
        <code>GET hoodies/me/</code> with <code>credentials: include</code>.
        CSRF cookie visibility in JavaScript is separate from whether the browser
        sends <code>csrf_token</code> on credentialed requests to the Django host.
        Token values are never displayed.
      </p>
      <dl className="cs-detail__dl cs-staging-session-dl">
        <dt>Auth status</dt>
        <dd>
          <code>{status}</code>
        </dd>
        <dt>Member</dt>
        <dd>{identity?.displayName || "—"}</dd>
        <dt>Last session HTTP</dt>
        <dd>{httpStatus ?? "—"}</dd>
        <CsrfCookieDiagnosticDisplay
          diagnostic={csrfDiagnostic}
          isAuthenticated={isAuthenticated}
        />
        <dt>Backend base URL</dt>
        <dd>
          <code>{isHoodieApiConfigured() ? HOODIE_API_BASE_URL : "(not configured)"}</code>
        </dd>
        <dt>Last checked</dt>
        <dd>
          {lastCheckedAt
            ? new Date(lastCheckedAt).toLocaleString()
            : "—"}
        </dd>
      </dl>
      <div className="cs-staging-session-actions">
        <button
          type="button"
          className="cs-btn cs-btn--ghost"
          disabled={refreshLoading}
          onClick={runSessionProbe}
        >
          Probe GET hoodies/me/
        </button>
        <button
          type="button"
          className="cs-btn cs-btn--ghost"
          disabled={refreshLoading}
          onClick={runRefresh}
        >
          Refresh auth + probe session
        </button>
      </div>
      <StagingTestResult result={refreshResult} label="Session probe" />
    </section>
  );
}

function SharedIdsPanel({ ids, setIds }) {
  return (
    <section className="cs-diag-section cs-staging-ids">
      <h2>Test IDs (manual — from staging Django admin or legacy UI)</h2>
      <p className="cs-diag-note">
        Enter real IDs from your staging environment. No IDs are prefilled or
        discovered automatically.
      </p>
      <div className="cs-staging-ids__grid">
        {[
          ["ownedListingId", "Owned listing ID"],
          ["nonOwnedListingId", "Non-owned listing ID"],
          ["ownedEventId", "Owned event ID"],
          ["nonOwnedEventId", "Non-owned event ID"],
          ["ownedPromoId", "Owned promo ID"],
          ["nonOwnedPromoId", "Non-owned promo ID"],
        ].map(([name, label]) => (
          <label key={name} className="cs-staging-input">
            <span>{label}</span>
            <input
              type="text"
              value={ids[name] || ""}
              placeholder="e.g. 123"
              onChange={(e) =>
                setIds((prev) => ({ ...prev, [name]: e.target.value }))
              }
            />
          </label>
        ))}
      </div>
    </section>
  );
}

export default function StagingVerificationPage() {
  const [ids, setIds] = useState({});

  return (
    <div className="cs-diagnostics cs-staging-page">
      <p>
        <Link to={CLASSIC_SITE_BASE}>← Classic Site home</Link>
        {" · "}
        <Link to={`${CLASSIC_SITE_BASE}/diagnostics/api-capabilities`}>
          API capability audit
        </Link>
      </p>

      <div className="cs-staging-banner" role="alert">
        <strong>Developer-only staging verification. Do not use in production.</strong>
        <p>
          Manual probes only. Mutations require explicit confirmation. A passing
          test does not mean product-ready CRUD UI.
        </p>
      </div>

      <h1>Staging verification (Phase 5)</h1>

      <section className="cs-diag-section">
        <SessionEnvironmentDiagnosticPanel showTestButtons />
      </section>

      <SessionCsrfSection />
      <SharedIdsPanel ids={ids} setIds={setIds} />

      <section className="cs-diag-section">
        <h2>2. Listing verification</h2>
        <StagingTestControl
          title="GET owned listing by ID"
          method="GET"
          pathDescription="listings/{ownedListingId}/"
          pathBuilder={(input) => `listings/${input.ownedListingId}/`}
          externalInputs={ids}
          runTest={() => stagingProbeGet(`listings/${ids.ownedListingId}/`)}
          disabled={!ids.ownedListingId}
        />
        <StagingTestControl
          title="GET non-owned listing by ID"
          method="GET"
          pathDescription="listings/{nonOwnedListingId}/"
          externalInputs={ids}
          runTest={() => stagingProbeGet(`listings/${ids.nonOwnedListingId}/`)}
          disabled={!ids.nonOwnedListingId}
        />
        <StagingTestControl
          title="PATCH owned listing (no-op business_name)"
          method="PATCH"
          pathDescription="listings/{ownedListingId}/"
          externalInputs={ids}
          requiresMutationConfirm
          runTest={() => stagingProbeListingNoOpPatch(ids.ownedListingId)}
          disabled={!ids.ownedListingId}
        />
        <StagingTestControl
          title="PATCH non-owned listing (expect 403/404)"
          method="PATCH"
          pathDescription="listings/{nonOwnedListingId}/"
          externalInputs={ids}
          requiresMutationConfirm
          runTest={async () => {
            const getResult = await stagingProbeGet(
              `listings/${ids.nonOwnedListingId}/`
            );
            const name =
              getResult.data?.business_name || "staging-non-owned-probe";
            return stagingProbePatchJson(`listings/${ids.nonOwnedListingId}/`, {
              business_name: name,
            });
          }}
          disabled={!ids.nonOwnedListingId}
        />
        <StagingTestControl
          title="DELETE owned listing (destructive)"
          method="DELETE"
          pathDescription="listings/{ownedListingId}/"
          externalInputs={ids}
          requiresMutationConfirm
          requiresDestructiveConfirm
          runTest={() => stagingProbeDelete(`listings/${ids.ownedListingId}/`)}
          disabled={!ids.ownedListingId}
        />
        <StagingTestControl
          title="POST listing (unsafe — expected to fail or create incomplete data)"
          method="POST"
          pathDescription="listings/"
          requiresMutationConfirm
          requiresUnsafeConfirm
          runTest={() =>
            stagingProbePostJson("listings/", {
              business_name: `staging-probe-${Date.now()}`,
            })
          }
        />
        <StagingTestControl
          title="PATCH listing graphic (multipart, optional)"
          method="PATCH"
          pathDescription="listings/{ownedListingId}/ (multipart graphic)"
          externalInputs={ids}
          requiresMutationConfirm
          pathBuilder={() => `listings/${ids.ownedListingId}/`}
          runTest={async (input) => {
            const file = input.graphicFile;
            if (!file) {
              return {
                ok: false,
                status: 0,
                method: "PATCH",
                path: `listings/${ids.ownedListingId}/`,
                fullUrl: "",
                data: null,
                errorMessage: "Select an image file first",
                credentialsIncluded: true,
                csrfCookiePresent: false,
                csrfHeaderSent: false,
                timestamp: new Date().toISOString(),
              };
            }
            const formData = new FormData();
            formData.append("graphic", file);
            return stagingProbePatchMultipart(
              `listings/${ids.ownedListingId}/`,
              formData
            );
          }}
          disabled={!ids.ownedListingId}
        >
          <StagingFileInput
            label="Image file (optional)"
            name="graphicFile"
            accept="image/*"
          />
        </StagingTestControl>
      </section>

      <section className="cs-diag-section">
        <h2>3. Event verification</h2>
        <p className="cs-diag-note">
          Public queryset filters active/future events only. No{" "}
          <code>?entry=</code> filter — list search uses business name text.
        </p>
        <StagingTestControl
          title="GET event by ID"
          method="GET"
          pathDescription="events/{ownedEventId}/"
          externalInputs={ids}
          runTest={() => stagingProbeGet(`events/${ids.ownedEventId}/`)}
          disabled={!ids.ownedEventId}
        />
        <StagingTestControl
          title="GET events list"
          method="GET"
          pathDescription="events/"
          runTest={() => stagingProbeGet("events/")}
        />
        <StagingTestControl
          title="GET events search (by listing business name — if known)"
          method="GET"
          pathDescription="events/?search={query}"
          requiresMutationConfirm={false}
          runTest={async (input) =>
            stagingProbeGet(
              `events/?search=${encodeURIComponent(input.searchQuery || "")}`
            )
          }
        >
          <StagingInput
            label="Search query (business name)"
            name="searchQuery"
            placeholder="Listing business name"
          />
        </StagingTestControl>
        <StagingTestControl
          title="PATCH owned event (no-op name)"
          method="PATCH"
          pathDescription="events/{ownedEventId}/"
          externalInputs={ids}
          requiresMutationConfirm
          runTest={() => stagingProbeEventNoOpPatch(ids.ownedEventId)}
          disabled={!ids.ownedEventId}
        />
        <StagingTestControl
          title="PATCH non-owned event (expect 403/404)"
          method="PATCH"
          pathDescription="events/{nonOwnedEventId}/"
          externalInputs={ids}
          requiresMutationConfirm
          runTest={async () => {
            const getResult = await stagingProbeGet(
              `events/${ids.nonOwnedEventId}/`
            );
            const name = getResult.data?.name || "staging-non-owned-probe";
            return stagingProbePatchJson(`events/${ids.nonOwnedEventId}/`, {
              name,
            });
          }}
          disabled={!ids.nonOwnedEventId}
        />
        <StagingTestControl
          title="POST event (unsafe — entry association unclear)"
          method="POST"
          pathDescription="events/"
          requiresMutationConfirm
          requiresUnsafeConfirm
          runTest={() =>
            stagingProbePostJson("events/", {
              name: `staging-event-probe-${Date.now()}`,
            })
          }
        />
        <StagingTestControl
          title="DELETE owned event (destructive)"
          method="DELETE"
          pathDescription="events/{ownedEventId}/"
          externalInputs={ids}
          requiresMutationConfirm
          requiresDestructiveConfirm
          runTest={() => stagingProbeDelete(`events/${ids.ownedEventId}/`)}
          disabled={!ids.ownedEventId}
        />
      </section>

      <section className="cs-diag-section">
        <h2>4. Promo verification</h2>
        <StagingTestControl
          title="GET promo by ID"
          method="GET"
          pathDescription="promos/{ownedPromoId}/"
          externalInputs={ids}
          runTest={() => stagingProbeGet(`promos/${ids.ownedPromoId}/`)}
          disabled={!ids.ownedPromoId}
        />
        <StagingTestControl
          title="GET promos list"
          method="GET"
          pathDescription="promos/"
          runTest={() => stagingProbeGet("promos/")}
        />
        <StagingTestControl
          title="GET promos search (by listing business name — if known)"
          method="GET"
          pathDescription="promos/?search={query}"
          runTest={async (input) =>
            stagingProbeGet(
              `promos/?search=${encodeURIComponent(input.searchQuery || "")}`
            )
          }
        >
          <StagingInput
            label="Search query (business name)"
            name="searchQuery"
            placeholder="Listing business name"
          />
        </StagingTestControl>
        <StagingTestControl
          title="PATCH owned promo (no-op name)"
          method="PATCH"
          pathDescription="promos/{ownedPromoId}/"
          externalInputs={ids}
          requiresMutationConfirm
          runTest={() => stagingProbePromoNoOpPatch(ids.ownedPromoId)}
          disabled={!ids.ownedPromoId}
        />
        <StagingTestControl
          title="PATCH non-owned promo (expect 403/404)"
          method="PATCH"
          pathDescription="promos/{nonOwnedPromoId}/"
          externalInputs={ids}
          requiresMutationConfirm
          runTest={async () => {
            const getResult = await stagingProbeGet(
              `promos/${ids.nonOwnedPromoId}/`
            );
            const name = getResult.data?.name || "staging-non-owned-probe";
            return stagingProbePatchJson(`promos/${ids.nonOwnedPromoId}/`, {
              name,
            });
          }}
          disabled={!ids.nonOwnedPromoId}
        />
        <StagingTestControl
          title="POST promo (unsafe — entry association unclear)"
          method="POST"
          pathDescription="promos/"
          requiresMutationConfirm
          requiresUnsafeConfirm
          runTest={() =>
            stagingProbePostJson("promos/", {
              name: `staging-promo-probe-${Date.now()}`,
            })
          }
        />
        <StagingTestControl
          title="DELETE owned promo (destructive)"
          method="DELETE"
          pathDescription="promos/{ownedPromoId}/"
          externalInputs={ids}
          requiresMutationConfirm
          requiresDestructiveConfirm
          runTest={() => stagingProbeDelete(`promos/${ids.ownedPromoId}/`)}
          disabled={!ids.ownedPromoId}
        />
      </section>

      <section className="cs-diag-section">
        <h2>5. Billing verification</h2>
        <p className="cs-diag-note">
          Billing remains Django-managed. This harness does not call Stripe or
          billing mutation endpoints.
        </p>
        <ul className="cs-hybrid-links">
          {BILLING_LEGACY_LINKS.map(({ label, path }) => (
            <li key={path}>
              <a href={path}>{label}</a> — <code>{path}</code>
            </li>
          ))}
        </ul>
      </section>

      <section className="cs-diag-section cs-diag-summary">
        <h2>Interpreting results</h2>
        <ul className="cs-verification__list">
          <li>
            <strong>GET 200</strong> — read path works for that ID/queryset
            filter.
          </li>
          <li>
            <strong>PATCH 200 on owned resource</strong> — member may be able to
            edit; still verify ownership enforcement separately.
          </li>
          <li>
            <strong>PATCH 403/404 on non-owned</strong> — expected if permissions
            work.
          </li>
          <li>
            <strong>POST listing/event/promo failure</strong> — expected per
            Phase 4 audit until serializer gaps resolved.
          </li>
          <li>
            <strong>CSRF missing on mutation</strong> — fix cookie/domain before
            any write UI.
          </li>
        </ul>
        <p className="cs-diag-note">
          Spec:{" "}
          <code>
            project-ops/specs/classic-site-phase-5-staging-verification-plan-v1.md
          </code>
        </p>
      </section>
    </div>
  );
}
